import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, setDoc, deleteDoc, collection, getDocs, writeBatch, query, where, getDoc } from 'firebase/firestore';

/**
 * Helper function to apply tag mappings to an array of tags
 * 
 * @param {Array} tags - Array of tag strings to process
 * @param {Object} mappings - Object with oldTag -> newTag mappings
 * @returns {Array} - Array of processed tags
 */
export const applyTagMappingsToArray = (tags, mappings) => {
  if (!tags || !Array.isArray(tags) || tags.length === 0 || !mappings || Object.keys(mappings).length === 0) {
    return tags;
  }
  
  // Helper function to recursively resolve mappings
  const resolveMappings = (tag) => {
    if (mappings[tag]) {
      return resolveMappings(mappings[tag]);
    }
    return tag;
  };
  
  // Map each tag to its final value and remove duplicates
  const mappedTags = [];
  const tagSet = new Set();
  
  tags.forEach(tag => {
    const mappedTag = resolveMappings(tag);
    if (!tagSet.has(mappedTag)) {
      tagSet.add(mappedTag);
      mappedTags.push(mappedTag);
    }
  });
  
  return mappedTags;
};

/**
 * Helper function to fetch tag mappings from Firestore
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Object with tag mappings
 */
export const fetchTagMappings = async (userId) => {
  if (!userId) return {};
  
  try {
    const tagMappingsRef = doc(db, "users", userId, "settings", "tagMappings");
    const tagMappingsDoc = await getDoc(tagMappingsRef);
    
    if (tagMappingsDoc.exists()) {
      return tagMappingsDoc.data().mappings || {};
    }
    return {};
  } catch (error) {
    console.error("Error fetching tag mappings:", error);
    return {};
  }
};

/**
 * TagManagementPanel component for managing practice tags
 * 
 * @param {Object} props
 * @param {Object} props.user - Current user object
 * @param {Function} props.getAllUniqueTags - Function to get all unique tags
 * @param {Array} props.sessionHistory - Array of practice sessions
 * @param {Function} props.setSessionHistory - Function to update session history
 * @param {Boolean} props.hasBasicAccess - Whether user has basic subscription access
 * @param {Function} props.onTagMappingsUpdated - Callback when tag mappings are updated
 */
const TagManagementPanel = ({
  user,
  getAllUniqueTags,
  sessionHistory,
  setSessionHistory,
  countSessions,
  hasBasicAccess,
  onTagMappingsUpdated
}) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTag, setEditingTag] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  const [mergeMode, setMergeMode] = useState(false);
  const [sourceTag, setSourceTag] = useState('');
  const [targetTag, setTargetTag] = useState('');
  const [processing, setProcessing] = useState(false);
  const [tagCounts, setTagCounts] = useState({});
  const [tagMappings, setTagMappings] = useState({});

  // Load tags on component mount
  useEffect(() => {
    if (user?.uid && hasBasicAccess) {
      loadTagMappings().then(() => {
        loadTags();
      });
    } else {
      loadTags();
    }
  }, [sessionHistory, countSessions, user?.uid]);

  // Load tag mappings from Firestore
  const loadTagMappings = async () => {
    if (!user?.uid || !hasBasicAccess) {
      setTagMappings({});
      return;
    }
    
    try {
      const mappings = await fetchTagMappings(user.uid);
      setTagMappings(mappings);
      return mappings;
    } catch (error) {
      console.error("Error loading tag mappings:", error);
      setTagMappings({});
      return {};
    }
  };

  // Apply tag mappings to ensure naming consistency
  const applyTagMappings = (tagList) => {
    if (!tagMappings || Object.keys(tagMappings).length === 0) {
      return tagList;
    }
    
    const mappedTags = new Set();
    const result = [];
    
    // Helper function to recursively resolve mappings
    const resolveMappings = (tag) => {
      if (tagMappings[tag]) {
        return resolveMappings(tagMappings[tag]);
      }
      return tag;
    };
    
    // Map tags to their final values and remove duplicates
    tagList.forEach(tag => {
      const mappedTag = resolveMappings(tag.name);
      
      if (!mappedTags.has(mappedTag)) {
        mappedTags.add(mappedTag);
        result.push({
          ...tag,
          name: mappedTag
        });
      }
    });
    
    return result;
  };

  // Load tags and calculate usage counts
  const loadTags = () => {
    setLoading(true);
    
    try {
      // Get all unique tags
      const uniqueTags = getAllUniqueTags();
      
      // Calculate usage counts over the full session set (countSessions) when
      // provided — falls back to the paginated sessionHistory otherwise.
      const counts = {};
      const countSource = (countSessions && countSessions.length) ? countSessions : sessionHistory;

      countSource.forEach(session => {
        if (!session.items) return;
        
        // Helper function to process items recursively
        const processItem = (item) => {
          if (item.tags && Array.isArray(item.tags)) {
            item.tags.forEach(tag => {
              counts[tag] = (counts[tag] || 0) + 1;
            });
          }
          
          // Process children recursively
          if (item.children && Array.isArray(item.children)) {
            item.children.forEach(processItem);
          }
        };
        
        // Process all top-level items
        session.items.forEach(processItem);
      });
      
      // Create tag objects with counts
      const tagObjects = uniqueTags.map(tag => ({
        name: tag,
        count: counts[tag] || 0
      }));
      
      // Apply tag mappings for consistency
      const mappedTags = applyTagMappings(tagObjects);
      
      // Sort by usage count (descending)
      mappedTags.sort((a, b) => b.count - a.count);
      
      setTags(mappedTags);
      setTagCounts(counts);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoading(false);
    }
  };

  // Start editing a tag
  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setNewTagName(tag.name);
    setMergeMode(false);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingTag(null);
    setNewTagName('');
  };

  // Save edited tag
  const handleSaveEdit = async () => {
    if (!newTagName.trim() || newTagName === editingTag.name) {
      handleCancelEdit();
      return;
    }
    
    // Check if user has basic access
    if (!user?.uid || !hasBasicAccess) {
      alert('You need a Basic or Pro subscription to edit tags.');
      handleCancelEdit();
      return;
    }
    
    setProcessing(true);
    
    try {
      // Update the tag in all sessions
      await updateTagInSessions(editingTag.name, newTagName.trim());
      
      // Store the tag mapping in Firestore
      await updateTagMappingInFirestore(editingTag.name, newTagName.trim());
      
      // Update local state
      setTags(prevTags => 
        prevTags.map(tag => 
          tag.name === editingTag.name 
            ? { ...tag, name: newTagName.trim() } 
            : tag
        )
      );
      
      // Reset editing state
      handleCancelEdit();
    } catch (error) {
      console.error('Error updating tag:', error);
      alert('Failed to update tag: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Toggle merge mode
  const handleToggleMergeMode = () => {
    setMergeMode(!mergeMode);
    setEditingTag(null);
    setSourceTag('');
    setTargetTag('');
  };

  // Merge tags
  const handleMergeTags = async () => {
    if (!sourceTag || !targetTag || sourceTag === targetTag) {
      alert('Please select different source and target tags.');
      return;
    }
    
    // Check if user has basic access
    if (!user?.uid || !hasBasicAccess) {
      alert('You need a Basic or Pro subscription to merge tags.');
      return;
    }
    
    // Confirm merge
    if (!window.confirm(`Are you sure you want to merge "${sourceTag}" into "${targetTag}"? This cannot be undone.`)) {
      return;
    }
    
    setProcessing(true);
    
    try {
      // Update the tag in all sessions (effectively merging)
      await updateTagInSessions(sourceTag, targetTag);
      
      // Store the tag mapping in Firestore
      await updateTagMappingInFirestore(sourceTag, targetTag);
      
      // Update local state
      setTags(prevTags => 
        prevTags
          .filter(tag => tag.name !== sourceTag)
          .map(tag => 
            tag.name === targetTag 
              ? { ...tag, count: (tagCounts[targetTag] || 0) + (tagCounts[sourceTag] || 0) } 
              : tag
          )
      );
      
      // Reset merge state
      setMergeMode(false);
      setSourceTag('');
      setTargetTag('');
      
      alert(`Successfully merged "${sourceTag}" into "${targetTag}".`);
    } catch (error) {
      console.error('Error merging tags:', error);
      alert('Failed to merge tags: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Store tag mapping in Firestore for consistency across devices
  const updateTagMappingInFirestore = async (oldTag, newTag) => {
    if (!user?.uid || !hasBasicAccess) return;
    
    try {
      // Get the reference to the tagMappings document
      const tagMappingsRef = doc(db, "users", user.uid, "settings", "tagMappings");
      
      // Try to get existing mappings
      const tagMappingsDoc = await getDoc(tagMappingsRef);
      let mappings = {};
      
      if (tagMappingsDoc.exists()) {
        mappings = tagMappingsDoc.data().mappings || {};
      }
      
      // Add new mapping - using the oldTag as the key and newTag as the value
      mappings[oldTag] = newTag;
      
      // Save to Firestore
      await setDoc(tagMappingsRef, { 
        mappings,
        updatedAt: new Date() 
      }, { merge: true });
      
      // Notify parent component about the updated mappings
      if (typeof onTagMappingsUpdated === 'function') {
        onTagMappingsUpdated(mappings);
      }
      
    } catch (error) {
      console.error('Error updating tag mapping in Firestore:', error);
      throw error;
    }
  };

  // Update tag name in all sessions (local and Firestore)
  const updateTagInSessions = async (oldTag, newTag) => {
    // Helper function to check if a session contains a specific tag
    const sessionContainsTag = (session, tagToFind) => {
      if (!session.items || !Array.isArray(session.items)) return false;
      
      const checkItem = (item) => {
        // Check if this item has the tag
        if (item.tags && Array.isArray(item.tags) && item.tags.includes(tagToFind)) {
          return true;
        }
        
        // Check children recursively
        if (item.children && Array.isArray(item.children)) {
          return item.children.some(child => checkItem(child));
        }
        
        return false;
      };
      
      return session.items.some(item => checkItem(item));
    };

    // First update local session history
    const updatedHistory = sessionHistory.map(session => {
      if (!session.items) return session;
      
      // Create a deep copy of the session
      const sessionCopy = { ...session, items: JSON.parse(JSON.stringify(session.items)) };
      let sessionUpdated = false;
      
      // Helper function to update tags in an item recursively
      const updateItemTags = (item) => {
        let itemUpdated = false;
        
        // Check if item has tags
        if (item.tags && Array.isArray(item.tags)) {
          const tagIndex = item.tags.indexOf(oldTag);
          
          // If old tag found, replace it
          if (tagIndex !== -1) {
            // Replace old tag with new tag, avoiding duplicates
            const newTags = [...item.tags];
            newTags.splice(tagIndex, 1);
            
            // Only add new tag if it doesn't already exist
            if (!newTags.includes(newTag)) {
              newTags.push(newTag);
            }
            
            item.tags = newTags;
            itemUpdated = true;
          }
        }
        
        // Process children
        if (item.children && item.children.length > 0) {
          item.children.forEach(child => {
            const childUpdated = updateItemTags(child);
            if (childUpdated) itemUpdated = true;
          });
        }
        
        return itemUpdated;
      };
      
      // Process all top-level items
      sessionCopy.items.forEach(item => {
        const itemUpdated = updateItemTags(item);
        if (itemUpdated) sessionUpdated = true;
      });
      
      // Return original or updated session
      return sessionUpdated ? sessionCopy : session;
    });
    
    // Update local state
    setSessionHistory(updatedHistory);
    
    // If user is signed in with basic access, update Firestore
    if (user?.uid && hasBasicAccess) {
      // Create a batch for multiple operations
      const batch = writeBatch(db);
      
      // Process each session to update in Firestore
      // Check the ORIGINAL session history, not the updated one
      for (let i = 0; i < sessionHistory.length; i++) {
        const originalSession = sessionHistory[i];
        const updatedSession = updatedHistory[i];
        
        // Only update sessions that actually contained the old tag
        if (sessionContainsTag(originalSession, oldTag)) {
          const sessionRef = doc(db, "users", user.uid, "sessionHistory", updatedSession.id);
          batch.set(sessionRef, updatedSession);
        }
      }
      
      // Update practice plans as well
      const plansQuery = query(collection(db, "users", user.uid, "practicePlans"));
      const plansSnapshot = await getDocs(plansQuery);
      
      plansSnapshot.forEach(planDoc => {
        const plan = planDoc.data();
        let planUpdated = false;
        
        // Skip plans with no items
        if (!plan.items || !Array.isArray(plan.items)) return;
        
        // Create deep copy of plan
        const planCopy = { ...plan, items: JSON.parse(JSON.stringify(plan.items)) };
        
        // Helper function to update tags in items recursively
        const updateItemTags = (item) => {
          let itemUpdated = false;
          
          // Check if item has tags
          if (item.tags && Array.isArray(item.tags)) {
            const tagIndex = item.tags.indexOf(oldTag);
            
            // If old tag found, replace it
            if (tagIndex !== -1) {
              // Replace old tag with new tag, avoiding duplicates
              const newTags = [...item.tags];
              newTags.splice(tagIndex, 1);
              
              // Only add new tag if it doesn't already exist
              if (!newTags.includes(newTag)) {
                newTags.push(newTag);
              }
              
              item.tags = newTags;
              itemUpdated = true;
            }
          }
          
          // Process children
          if (item.children && item.children.length > 0) {
            item.children.forEach(child => {
              const childUpdated = updateItemTags(child);
              if (childUpdated) itemUpdated = true;
            });
          }
          
          return itemUpdated;
        };
        
        // Process all top-level items
        planCopy.items.forEach(item => {
          const itemUpdated = updateItemTags(item);
          if (itemUpdated) planUpdated = true;
        });
        
        // Add to batch if updated
        if (planUpdated) {
          batch.set(planDoc.ref, planCopy);
        }
      });
      
      // Commit all changes
      await batch.commit();
    }
  };

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <p className="text-sm text-gray-600">
          {tags.length} unique tags found
        </p>
        
        <div className="flex space-x-2">
          {!mergeMode ? (
            <button
              onClick={handleToggleMergeMode}
              className="badge badge-primary text-xs rounded-md transition-colors hover:bg-brand-100"
              disabled={tags.length < 2 || processing}
            >
              Merge Tags
            </button>
          ) : (
            <button
              onClick={handleToggleMergeMode}
              className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
              disabled={processing}
            >
              Cancel Merge
            </button>
          )}
        </div>
      </div>
      
      {/* Merge UI */}
      {mergeMode && (
        <div className="mb-4 p-3 bg-brand-50 border border-brand-200 rounded-lg">
          <h4 className="font-medium mb-2">Merge Tags</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium mb-1 text-gray-700">
                Source Tag (will be removed)
              </label>
              <select 
                value={sourceTag} 
                onChange={(e) => setSourceTag(e.target.value)}
                className="w-full p-1.5 text-sm border border-gray-300 rounded"
                disabled={processing}
              >
                <option value="">Select a tag</option>
                {tags.map(tag => (
                  <option 
                    key={`source-${tag.name}`} 
                    value={tag.name}
                    disabled={tag.name === targetTag}
                  >
                    {tag.name} ({tag.count})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 text-gray-700">
                Target Tag (will remain)
              </label>
              <select 
                value={targetTag} 
                onChange={(e) => setTargetTag(e.target.value)}
                className="w-full p-1.5 text-sm border border-gray-300 rounded"
                disabled={processing}
              >
                <option value="">Select a tag</option>
                {tags.map(tag => (
                  <option 
                    key={`target-${tag.name}`} 
                    value={tag.name}
                    disabled={tag.name === sourceTag}
                  >
                    {tag.name} ({tag.count})
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button 
              onClick={handleMergeTags}
              className="px-3 py-1.5 text-sm bg-brand-500 text-white rounded hover:bg-brand-600 disabled:opacity-50"
              disabled={!sourceTag || !targetTag || sourceTag === targetTag || processing}
            >
              {processing ? 'Merging...' : 'Merge Tags'}
            </button>
          </div>
          
          <div className="mt-2 text-xs text-gray-500">
            <p>Merging will replace all instances of the source tag with the target tag.</p>
            <p>This action cannot be undone.</p>
          </div>
        </div>
      )}
      
      {/* Tag list */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600">Loading tags...</span>
        </div>
      ) : tags.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No tags found in your practice sessions.</p>
          <p className="text-sm mt-1">Add tags to your practice blocks to start organizing.</p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-md overflow-auto mt-4">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-surface-sunken">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                  Tag
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                  Count
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tags.map(tag => (
                <tr key={tag.name} className={editingTag?.name === tag.name ? 'bg-blue-50' : ''}>
                  <td className="px-3 py-3 text-sm">
                    {editingTag?.name === tag.name ? (
                      <input 
                        type="text"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        className="p-1 border border-gray-300 rounded w-full"
                        autoFocus
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[180px] md:max-w-none">
                        {tag.name}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-sm text-gray-500">{tag.count}</div>
                  </td>
                  <td className="px-3 py-3 text-right text-sm font-medium">
                    {editingTag?.name === tag.name ? (
                      <div className="flex space-x-2 justify-end">
                        <button 
                          onClick={handleSaveEdit}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          disabled={processing || !newTagName.trim() || newTagName === tag.name}
                        >
                          Save
                        </button>
                        <button 
                          onClick={handleCancelEdit}
                          className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                          disabled={processing}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button 
                        onClick={() => handleEditTag(tag)}
                        className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                        disabled={processing || mergeMode || !!editingTag}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Help text */}
      <div className="mt-4 text-sm text-gray-500">
        <p>Tip: You can use tag management to standardize your tag naming. For example, change "scales" to "Scales" for consistency.</p>
        <p>Note: Tag changes will affect all practice sessions and plans.</p>
      </div>
      
      {/* Subscription CTA for non-subscribers */}
      {user?.uid && !hasBasicAccess && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
          <p className="text-yellow-800 font-medium">Subscribe to edit and manage tags</p>
          <p className="text-yellow-700 mt-1">Tag management requires a Basic or Pro subscription.</p>
        </div>
      )}
    </div>
  );
};

export default TagManagementPanel; 